/**
 * @voltxampere/engine — Anderson-II acceleration (Walker & Ni 2011)
 *
 * **STATUS (Sprint 8b.1): math correctness PARTIALLY FIXED, behaviour
 * still not validated for production use.** The original Sprint 8b
 * shipping implementation used `g_j = x_j − f_j` (Picard fixed-point
 * form) for the `ΔG` matrix construction, which is dimensionally
 * wrong for a Newton-style iteration where `G(x) = x − J⁻¹·f(x)`
 * (the Newton step result, not `x − f`). Sprint 8b.1 corrects the
 * derivation to store the past Newton iterates (`xNewton_j`) directly
 * as the `G_j` history.
 *
 * Anderson acceleration (also called Pulay mixing or DIIS in the
 * quantum-chemistry literature) is a fixed-point iteration speed-up
 * that can break Newton limit cycles on ill-conditioned nonlinear
 * systems.
 *
 * Algorithm (Walker & Ni 2011, Type II for Newton iterations):
 *
 *   We store the last m past iterates `x_{k-m+1} … x_k`, their
 *   residuals `f_{k-m+1} … f_k`, AND the Newton steps that produced
 *   the next iterate (`xNewton_j = G(x_j)` for each `j`). The
 *   accelerated step is
 *
 *     x_{k+1} = xNewton_k - ΔG · γ
 *
 *   where `ΔG[:, j] = xNewton_{j+1} - xNewton_j` and `γ` is the
 *   least-squares minimiser of `|| Δf · γ − f_k ||`.
 *
 *   When Newton oscillates in a limit cycle, the last m residuals
 *   point in roughly opposite directions; the LS projection finds a
 *   γ that cancels the oscillation and produces a step normal to
 *   the cycle's axis.
 *
 * This module implements the simplest useful variant:
 *
 *   - Window size `m` (default 3).
 *   - `qrSolve` (Householder) from `@voltxampere/math` handles the
 *     `(n × m-1)` least-squares problem.
 *   - Residual-reject safety: caller supplies `f(xAccel)` (re-built
 *     from a fresh MNA pass) and we compare against `f_k`. If the
 *     accelerated step grows the residual we discard it.
 *   - Restart every `restartK` iterations clears history to avoid
 *     quasi-singular LS matrices as the iterate approaches a fixed
 *     point.
 *   - Rank-deficient LS (QR diagonals below `rcond`) also triggers
 *     fallback to Newton.
 *
 * Each call to `step()` consumes `(x_k, f_k, xNewton_k,
 * nodeIndexSet)` and returns the accelerated step PROPOSAL. The
 * caller MUST verify the proposal by evaluating `f(proposal)` and
 * passing the verdict back through `commit(accepted, ...)` so the
 * accelerator knows whether to keep this step in history.
 *
 * Sprint 8b.1 plumbs this corrected math + caller-side residual
 * verification through `anderson-newton.ts`. Despite the math fix,
 * Anderson does NOT close the PNP CE limit cycle — the Sprint 8b
 * agent review traced that to a separate cold-start trap (BJT-off
 * trivial fixed point, see `/tmp/pnp-8c-diag.md`). Anderson stays
 * shipped as opt-in (`SimulationSettings.dc.enableAnderson` default
 * `false`) until Sprint 9 observational validation on a circuit
 * where it actually helps.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError, SingularMatrixError } from "@voltxampere/shared";
import { createMatrix, matrixSet, qrSolve } from "@voltxampere/math";
export const DEFAULT_ANDERSON_OPTIONS = {
    m: 3,
    restartK: 10,
    rcond: 1e-12,
};
/**
 * Stateful Anderson-II accelerator. Rotate through iterates via
 * `step()`; reset between DC solve attempts via `reset()`.
 */
export class AndersonAccelerator {
    m;
    restartK;
    rcond;
    /** History of iterates `x_{k-m+1} … x_k`, oldest first. */
    history = [];
    /** History of residuals `f_{k-m+1} … f_k`, same order. */
    residuals = [];
    /**
     * Sprint 8b.1: history of past **Newton steps**
     * `xNewton_{k-m+1} … xNewton_k` (one per `step()` call). This is
     * the actual `G(x_j) = x_j − J⁻¹·f(x_j)` map output, used to
     * build the `ΔG` matrix correctly. The Sprint 8b implementation
     * approximated `G_j ≈ x_j − f_j` (Picard form), which is
     * dimensionally wrong for Newton iterations and produced 1e10-V
     * blow-ups on PNP CE.
     */
    newtonSteps = [];
    iterationCount = 0;
    constructor(opts = {}) {
        const { m, restartK, rcond } = { ...DEFAULT_ANDERSON_OPTIONS, ...opts };
        if (!Number.isInteger(m) || m < 2) {
            throw new ContractViolationError(`AndersonAccelerator: m must be an integer ≥ 2, got ${String(m)}`);
        }
        if (!Number.isInteger(restartK) || restartK < 1) {
            throw new ContractViolationError(`AndersonAccelerator: restartK must be a positive integer, got ${String(restartK)}`);
        }
        if (!Number.isFinite(rcond) || rcond < 0) {
            throw new ContractViolationError(`AndersonAccelerator: rcond must be a non-negative finite number, got ${String(rcond)}`);
        }
        this.m = m;
        this.restartK = restartK;
        this.rcond = rcond;
    }
    /** Clear history — call between solve attempts. */
    reset() {
        this.history.length = 0;
        this.residuals.length = 0;
        this.newtonSteps.length = 0;
        this.iterationCount = 0;
    }
    /** Number of past iterates currently retained. */
    get historyLength() {
        return this.history.length;
    }
    /**
     * Produce the accelerated iterate given the current iterate
     * `x_k`, its residual `f_k = f(x_k)`, and the Newton step
     * `xNewton = G(x_k) = x_k - J⁻¹·f(x_k)`.
     *
     * Returns `{ xNext, accelerated }` where `xNext` is the iterate
     * to adopt and `accelerated` is `true` iff the Anderson step was
     * used (as opposed to falling back to the plain Newton step).
     *
     * Fallback to Newton happens when:
     *   - History is too short (`historyLength < 2`).
     *   - The LS matrix is rank-deficient (`qrSolve` throws).
     *   - The accelerated step's residual projection (estimated via
     *     the linear coefficients — we do not re-evaluate `f`) is
     *     WORSE than the plain Newton step. This is the Walker & Ni
     *     "residual-monitoring" safety net.
     *
     * @param xk        Current iterate `x_k`.
     * @param fk        Residual `f(x_k)` evaluated on the full MNA
     *                  system (length must equal `x_k.length`).
     * @param xNewton   Newton step from `x_k`, i.e. the would-be next
     *                  iterate if Anderson were absent.
     * @param _nodeIndexSet  Reserved for future per-node residual
     *   monitoring inside the accelerator. Sprint 8b.1 moved residual
     *   verification to the caller (anderson-newton.ts) so this
     *   parameter is unused — kept on the signature so callers do
     *   not need to be re-edited when residual monitoring returns.
     */
    step(xk, fk, xNewton, _nodeIndexSet) {
        const n = xk.length;
        if (fk.length !== n || xNewton.length !== n) {
            throw new ContractViolationError(`AndersonAccelerator.step: length mismatch (xk=${String(n)}, fk=${String(fk.length)}, xNewton=${String(xNewton.length)})`);
        }
        this.iterationCount++;
        // Periodic restart (Walker & Ni Type II: the LS matrix can go
        // quasi-singular once the orbit shrinks, a fresh restart
        // recovers).
        if (this.iterationCount % this.restartK === 0) {
            this.reset();
            this.iterationCount = 1; // preserve "non-zero" state
        }
        // Append new sample. Sprint 8b.1: ALSO store xNewton (the
        // actual Newton step result = G(x_k)) so the next iter's ΔG
        // construction uses the dimensionally-correct quantity.
        this.history.push(new Float64Array(xk));
        this.residuals.push(new Float64Array(fk));
        this.newtonSteps.push(new Float64Array(xNewton));
        while (this.history.length > this.m) {
            this.history.shift();
            this.residuals.shift();
            this.newtonSteps.shift();
        }
        const historyLen = this.history.length;
        if (historyLen < 2) {
            // Not enough history to accelerate — take plain Newton step.
            return { xNext: new Float64Array(xNewton), accelerated: false };
        }
        // Build the LS problem: minimise ||ΔF · γ - f_k|| where ΔF is
        // the (n × m-1) matrix of residual differences
        //   ΔF[:, j] = f_{k-m+j+1} - f_{k-m+j}
        // (one fewer column than the history length).
        const cols = historyLen - 1;
        const F = createMatrix(n, cols);
        for (let j = 0; j < cols; j++) {
            const fNewer = this.residuals[j + 1] ?? new Float64Array(n);
            const fOlder = this.residuals[j] ?? new Float64Array(n);
            for (let i = 0; i < n; i++) {
                matrixSet(F, i, j, (fNewer[i] ?? 0) - (fOlder[i] ?? 0));
            }
        }
        const lastResidual = this.residuals[historyLen - 1] ?? new Float64Array(n);
        const rhsLS = new Float64Array(lastResidual);
        let gamma;
        try {
            gamma = qrSolve(F, rhsLS, this.rcond);
        }
        catch (e) {
            if (e instanceof SingularMatrixError || e instanceof ContractViolationError) {
                // Rank-deficient LS (Sprint 8b expected: once the orbit
                // stabilises the residual differences span a lower-rank
                // subspace). Fall back to Newton step.
                return { xNext: new Float64Array(xNewton), accelerated: false };
            }
            throw e;
        }
        // Sprint 8b.1: corrected accelerated step.
        //   x_{k+1} = xNewton_k - (ΔG · γ)
        // where ΔG[:, j] = G_{j+1} − G_j and G_j = xNewton_j (the
        // actual Newton step result = `x_j − J(x_j)⁻¹·f(x_j)`).
        //
        // The Sprint 8b shipped form approximated `G_j ≈ x_j − f_j`,
        // which is the Picard fixed-point formulation. For a Newton
        // iteration `J⁻¹·f` and `f` differ by `||J⁻¹|| ≈ 1/g_m` (the
        // BJT transconductance ≈ 76 mS at the PNP CE OP). The Picard
        // approximation thus over-extrapolates by ~10× per Newton step
        // and ~10² per Anderson combination, producing the 1e10-volt
        // accelerated iterates that Sprint 8b's review found.
        const xAccel = new Float64Array(xNewton);
        for (let j = 0; j < cols; j++) {
            const newtonNewer = this.newtonSteps[j + 1] ?? new Float64Array(n);
            const newtonOlder = this.newtonSteps[j] ?? new Float64Array(n);
            const g = gamma[j] ?? 0;
            if (g === 0)
                continue;
            for (let i = 0; i < n; i++) {
                const dG = (newtonNewer[i] ?? 0) - (newtonOlder[i] ?? 0);
                xAccel[i] = (xAccel[i] ?? 0) - g * dG;
            }
        }
        // Sprint 8b.1 safety net (Fix #2): the original Sprint 8b
        // safety net compared `||f_accel||` (the LS-estimated residual
        // = `||f_k − ΔF·γ||`) against `||f_k||`. By LS construction the
        // estimate is ALWAYS ≤ ||f_k||, so the check structurally
        // passed regardless of how badly `xAccel` had blown up in
        // iterate space (Agent 1's P0-2 finding). The fix is to defer
        // the residual check to the CALLER who has the MNA build
        // capability — `step()` returns the proposal, the caller
        // evaluates `f(xAccel)` via a fresh build, and the caller
        // decides whether to commit the accepted iterate to history
        // through `commitAccepted()` / `revertLast()`.
        //
        // To keep the accelerated proposal from going wildly far in
        // iterate space we ALSO apply a magnitude sanity check here:
        // if `||xAccel − xNewton||∞` exceeds 10× the Newton step's
        // magnitude, the LS extrapolation is in territory the
        // linearisation cannot trust — fall back to plain Newton.
        const SANITY_FACTOR = 10;
        let newtonStepMax = 0;
        let andersonExtraMax = 0;
        for (let i = 0; i < n; i++) {
            const ns = Math.abs((xNewton[i] ?? 0) - (xk[i] ?? 0));
            if (ns > newtonStepMax)
                newtonStepMax = ns;
            const ae = Math.abs((xAccel[i] ?? 0) - (xNewton[i] ?? 0));
            if (ae > andersonExtraMax)
                andersonExtraMax = ae;
        }
        if (andersonExtraMax > SANITY_FACTOR * Math.max(newtonStepMax, 1e-3)) {
            // Anderson extrapolation way outside Newton's radius — defer.
            return { xNext: new Float64Array(xNewton), accelerated: false };
        }
        return { xNext: xAccel, accelerated: true };
    }
    /**
     * Sprint 8b.1: caller-side residual verification path. After
     * `step()` returns an accelerated proposal, the caller builds
     * `f(xAccel)` via a fresh MNA pass and compares its node-KCL
     * norm against the pre-step `f(xk)` norm. If the proposal grew
     * the residual, the caller calls `revertLast()` to drop this
     * iterate from history (so the next `step()` call is not built
     * on top of a bad sample).
     *
     * If the proposal IS accepted, no action needed — the entry
     * already lives in history from `step()`.
     */
    revertLast() {
        if (this.history.length > 0) {
            this.history.pop();
            this.residuals.pop();
            this.newtonSteps.pop();
        }
    }
}
//# sourceMappingURL=anderson.js.map